import React, { useRef } from 'react';
import { alpha, Fab, useTheme } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DSBox from '../../../design-system/DSBox';
import { DSTypography } from '../../../design-system/Typography';
import DSTextField from '../../../design-system/DSTextField';
import DSPrimaryButton from '../../../design-system/DSPrimaryButton';
import DSSecondaryButton from '../../../design-system/DSSecondaryButton';
import DSCircularProgress from '../../../design-system/DSCircularProgress';
import DSAvatar from '../../../design-system/DSAvatar';
import CountryDropdown from '../../../components/CountryDropdown/components/CountryDropdown';
import { SOCIAL_NETWORKS } from '../../../types/socialMedia';
import { isValidSocialLink } from '../../../utils/socialMedia';
import {
  USER_TITLE_LIMIT,
  USER_DESCRIPTION_LIMIT,
  USER_NAME_LIMIT,
} from '../../../consts/textFieldsLimitations';
import {
  COVER_ASPECT_RATIO,
  COVER_CONTENT_OVERLAP,
  COVER_UPLOAD_HINT,
  getCoverBackground,
  PRIMARY_BUTTON_HOVER_COLOR,
} from '../consts';
import useDominantColor from '../hooks/useDominantColor';
import { EditArtistIdentityState } from '../hooks/useEditArtistIdentity';
import { getScaledCloudinaryUrl } from '../../../utils';

type ArtistIdentityEditProps = Omit<
  EditArtistIdentityState,
  'isEditing' | 'handleStartEditing'
>;

const LABEL_SX = {
  fontFamily: 'Gilroy-SemiBold',
  fontSize: '15px',
  lineHeight: 1.2,
  letterSpacing: '0.15px',
  color: 'common.white',
  mt: '10px',
  mb: '10px',
};

const ArtistIdentityEdit: React.FC<ArtistIdentityEditProps> = ({
  editName,
  setEditName,
  editShortDescription,
  setEditShortDescription,
  editDescription,
  setEditDescription,
  editCountry,
  setEditCountry,
  editPhotoUrl,
  editCoverUrl,
  editSocialMedia,
  setEditSocialMedia,
  handleCoverUpload,
  handleProfilePhotoUpload,
  handleCancel,
  handleSave,
  disabledSaveButton,
  isUploadingCover,
  isUploadingPhoto,
  isSaving,
  showValidationErrors,
  requireAllFields,
  unreachableWebsite,
}) => {
  const { spacing, palette } = useTheme();
  const coverInputRef = useRef<HTMLInputElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const dominantColor = useDominantColor(
    !editCoverUrl ? editPhotoUrl : undefined,
  );
  const coverBackground = getCoverBackground(
    editCoverUrl,
    dominantColor,
    palette.background.default,
  );

  const handleSocialLinkChange = (networkName: string, link: string) => {
    const updated = editSocialMedia.filter(
      (item) => item.network.toLowerCase() !== networkName.toLowerCase(),
    );
    if (link.trim()) {
      updated.push({ network: networkName, link });
    }
    setEditSocialMedia(updated);
  };

  return (
    <DSBox
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: palette.background.paper,
        borderRadius: '16px',
        overflow: 'hidden',
        minWidth: '270px',
      }}
    >
      {/* Cover Image with Hover Overlay */}
      <DSBox
        sx={{
          position: 'relative',
          aspectRatio: COVER_ASPECT_RATIO,
          background: coverBackground,
          overflow: 'hidden',
          mb: `-${COVER_CONTENT_OVERLAP}`,
        }}
      >
        {editCoverUrl && (
          <img
            src={getScaledCloudinaryUrl(editCoverUrl, 1000)}
            alt="Cover"
            loading="eager"
            decoding="async"
            fetchPriority="high"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        )}

        <DSBox
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: alpha(palette.common.black, 0.5),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pb: COVER_CONTENT_OVERLAP,
            cursor: 'pointer',
            opacity: 0,
            transition: 'opacity 0.2s',
            '&:hover': { opacity: 1 },
          }}
          onClick={() => coverInputRef.current?.click()}
        >
          <CloudUploadOutlinedIcon
            sx={{
              color: 'common.white',
              fontSize: 32,
              display: { xxs: 'none', sm: 'block' },
            }}
          />
          <DSTypography
            variant="body2"
            color="common.white"
            sx={{ mt: spacing(0.5) }}
          >
            {isUploadingCover ? 'Uploading...' : 'Change Cover'}
          </DSTypography>
          <DSTypography variant="body2">{COVER_UPLOAD_HINT}</DSTypography>
        </DSBox>

        {/* Editing chip */}
        <Fab
          variant="extended"
          size="small"
          sx={{
            position: 'absolute',
            top: { xxs: '12px', sm: '42px' },
            left: { xxs: '12px', sm: '42px' },
            backgroundColor: alpha(palette.common.white, 0.2),
            color: 'common.white',
            fontFamily: 'Gilroy-SemiBold',
            fontSize: '13px',
            letterSpacing: '0.16px',
            lineHeight: '18px',
            px: '10px',
            py: '3px',
            minHeight: '24px',
            height: '24px',
            borderRadius: '100px',
            boxShadow: 'none',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: alpha(palette.common.white, 0.3),
              boxShadow: 'none',
            },
          }}
        >
          Editing
        </Fab>

        <input
          ref={coverInputRef}
          type="file"
          accept="image/jpeg,image/png"
          hidden
          onChange={handleCoverUpload}
        />
      </DSBox>

      {/* Card content below cover */}
      <DSBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '35px',
          px: { xxs: '24px', sm: '40px', md: '120px' },
          pb: '48px',
        }}
      >
        {/* Avatar with Hover Overlay */}
        <DSBox>
          <DSBox
            sx={{
              position: 'relative',
              width: '128px',
              height: '128px',
            }}
          >
            <DSAvatar
              src={
                editPhotoUrl
                  ? getScaledCloudinaryUrl(editPhotoUrl, 256)
                  : undefined
              }
              alt={editName}
              sx={{
                width: '128px',
                height: '128px',
                border: `4px solid ${palette.background.paper}`,
              }}
            />
            <DSBox
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                backgroundColor: alpha(palette.common.black, 0.5),
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                opacity: 0,
                transition: 'opacity 0.2s',
                '&:hover': { opacity: 1 },
              }}
              onClick={() => photoInputRef.current?.click()}
            >
              <CloudUploadOutlinedIcon
                sx={{ color: 'common.white', fontSize: 20 }}
              />
              <DSTypography
                variant="caption"
                color="common.white"
                sx={{ mt: spacing(0.25) }}
              >
                {isUploadingPhoto ? 'Uploading...' : 'Change'}
              </DSTypography>
            </DSBox>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/png"
              hidden
              onChange={handleProfilePhotoUpload}
            />
          </DSBox>
          {showValidationErrors && requireAllFields && !editPhotoUrl && (
            <DSTypography variant="caption" sx={{ color: 'error.main' }}>
              Profile photo is required
            </DSTypography>
          )}
        </DSBox>

        {/* Form Fields */}
        <DSBox
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xxs: '32px', sm: '64px' },
          }}
        >
          {/* Group 1: Public Name, Title, Country */}
          <DSBox
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <DSBox>
              <DSTypography sx={LABEL_SX}>
                User Name (max {USER_NAME_LIMIT} characters)
              </DSTypography>
              <DSTextField
                fullWidth
                value={editName}
                onChange={(event) => setEditName(event.target.value)}
                placeholder="Username"
                inputProps={{ maxLength: USER_NAME_LIMIT }}
                error={showValidationErrors && !editName.trim()}
                helperText={
                  showValidationErrors && !editName.trim()
                    ? 'Name is required'
                    : undefined
                }
              />
            </DSBox>
            <DSBox>
              <DSTypography sx={LABEL_SX}>Title</DSTypography>
              <DSTextField
                fullWidth
                value={editShortDescription}
                onChange={(event) =>
                  setEditShortDescription(event.target.value)
                }
                placeholder="Max 4 Words"
                inputProps={{ maxLength: USER_TITLE_LIMIT }}
              />
            </DSBox>
            <DSBox sx={{ '& .MuiAutocomplete-root': { marginLeft: 0 } }}>
              <CountryDropdown
                initialCountryCode={editCountry}
                onSelect={(countryCode) => setEditCountry(countryCode)}
                error={showValidationErrors && requireAllFields && !editCountry}
                helperText={
                  showValidationErrors && requireAllFields && !editCountry
                    ? 'Country is required'
                    : undefined
                }
              />
            </DSBox>
          </DSBox>

          {/* Group 2: Description */}
          <DSBox
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <DSBox>
              <DSTypography sx={LABEL_SX}>Description</DSTypography>
              <DSTextField
                fullWidth
                multiline
                minRows={5}
                value={editDescription}
                onChange={(event) => setEditDescription(event.target.value)}
                placeholder={`Detailed biography (max ${USER_DESCRIPTION_LIMIT} characters)`}
                inputProps={{ maxLength: USER_DESCRIPTION_LIMIT }}
                error={
                  showValidationErrors &&
                  requireAllFields &&
                  !editDescription.trim()
                }
                helperText={
                  showValidationErrors &&
                  requireAllFields &&
                  !editDescription.trim()
                    ? 'Description is required'
                    : undefined
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '16px',
                  },
                }}
              />
            </DSBox>
          </DSBox>

          {/* Group 3: Online Presence */}
          <DSBox
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <DSTypography sx={{ ...LABEL_SX, mb: 0 }}>
              Online Presence
            </DSTypography>
            <DSBox
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              {SOCIAL_NETWORKS.map(
                ({ name: networkName, icon: IconComponent }) => {
                  const existingLink =
                    editSocialMedia.find(
                      (item) =>
                        item.network.toLowerCase() ===
                        networkName.toLowerCase(),
                    )?.link || '';

                  const isWebsite = networkName === 'Website';
                  const formatError =
                    !!existingLink.trim() &&
                    !isValidSocialLink(networkName, existingLink);
                  const reachabilityError =
                    isWebsite &&
                    !!unreachableWebsite &&
                    existingLink.trim() === unreachableWebsite;
                  const showLinkError = formatError || reachabilityError;

                  return (
                    <DSBox
                      key={networkName}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <DSBox
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '35px',
                          height: '35px',
                          flexShrink: 0,
                        }}
                      >
                        <IconComponent
                          color={palette.text.secondary}
                          width="24px"
                          height="24px"
                        />
                      </DSBox>
                      <DSTextField
                        fullWidth
                        value={existingLink}
                        onChange={(event) =>
                          handleSocialLinkChange(
                            networkName,
                            event.target.value,
                          )
                        }
                        placeholder={networkName}
                        error={showLinkError}
                        helperText={
                          reachabilityError
                            ? 'This link is unreachable (e.g. 404). Please use a working URL.'
                            : formatError
                              ? isWebsite
                                ? 'Enter a valid website URL'
                                : `Enter a valid ${networkName} handle or link`
                              : undefined
                        }
                      />
                    </DSBox>
                  );
                },
              )}
            </DSBox>
          </DSBox>
        </DSBox>

        {/* Divider */}
        <DSBox
          sx={{
            height: '1px',
            backgroundColor: alpha(palette.common.white, 0.1),
          }}
        />

        {/* Action Buttons */}
        <DSBox
          sx={{
            display: 'flex',
            flexDirection: { xxs: 'column-reverse', sm: 'row' },
            justifyContent: 'flex-end',
            gap: '12px',
          }}
        >
          <DSSecondaryButton
            variant="outlined"
            onClick={handleCancel}
            sx={{
              color: 'common.white',
              borderColor: alpha(palette.common.white, 0.56),
              borderWidth: '1px',
              borderRadius: '1000px',
              px: { xxs: '24px', sm: '40px' },
              py: { xxs: '10px', sm: '14px' },
              fontFamily: 'Gilroy-Bold',
              fontSize: '16px',
              lineHeight: '26px',
              letterSpacing: '0.46px',
              textTransform: 'capitalize',
              '&:hover': {
                backgroundColor: alpha(palette.common.white, 0.1),
                borderColor: alpha(palette.common.white, 0.7),
              },
            }}
          >
            Cancel
          </DSSecondaryButton>
          <DSPrimaryButton
            onClick={handleSave}
            disabled={disabledSaveButton || isSaving}
            sx={{
              borderRadius: '1000px',
              px: { xxs: '24px', sm: '40px' },
              py: { xxs: '10px', sm: '14px' },
              fontFamily: 'Gilroy-Bold',
              fontSize: '16px',
              lineHeight: '26px',
              letterSpacing: '0.46px',
              textTransform: 'capitalize',
              boxShadow: 2,
              '&:hover:not(:disabled)': {
                backgroundColor: PRIMARY_BUTTON_HOVER_COLOR,
              },
            }}
          >
            {isSaving ? (
              <DSCircularProgress size={24} sx={{ color: 'inherit' }} />
            ) : (
              'Save changes'
            )}
          </DSPrimaryButton>
        </DSBox>
      </DSBox>
    </DSBox>
  );
};

export default ArtistIdentityEdit;
